import React, { useState } from 'react';
import styled from '@emotion/native';
import { Image, ScrollView, useWindowDimensions } from 'react-native';
import {
  lightGreyAlt,
  disabledLightGrey,
  green,
  lightGreen,
  mintGreenAlt,
  red,
  white,
} from '@/shared/styles/colors';
import Button from '@/shared/components/Button';
import StaticInput from '@/shared/components/StaticInput';
import { BaseText } from '@/shared/components/StyledText';
import { IS_WEB } from '@/shared/constants/utility';
import { DatePickerInput } from 'react-native-paper-dates';
import { openImagePicker } from '@/shared/utils';
import SentryService from '@/services/sentry/Sentry.service';
import useStore from '@/stores/useStore';
import { useMutation } from '@apollo/client';
import { SUBMIT_SOURCED_BY_REQUEST } from '@/shared/mutations';
import { SubmitSourcedByRequestInput } from '@/shared/types/generated';
import { useImageUpload } from '@/shared/hooks/useImageUpload';
import { useAuth } from '@/services/auth/AuthProvider';

interface SourcedByFormProps {
  onSubmitSuccess: () => void;
  isDesktop: boolean;
  clientUserId: number | undefined;
}

export const SourcedByForm: React.FC<SourcedByFormProps> = ({
  onSubmitSuccess,
  isDesktop,
  clientUserId,
}) => {
  const setSnackbarState = useStore((state) => state.setSnackbarState);
  const { getAccessToken } = useAuth();
  const { uploadImage, isUploading } = useImageUpload();

  const [submitSourcedByRequest] = useMutation<
    { submitSourcedByRequest: boolean },
    { input: SubmitSourcedByRequestInput }
  >(SUBMIT_SOURCED_BY_REQUEST);

  const [productName, setProductName] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [uploadedImageUri, setUploadedImageUri] = useState('');
  const [size, setSize] = useState('');
  const [color, setColor] = useState('');
  const [needByDate, setNeedByDate] = useState<Date | undefined>(undefined);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { height: windowHeight } = useWindowDimensions();
  const maxInputHeight = Math.min(windowHeight * 0.5, 400);

  const isValidUrl = (url: string) => /^https?:\/\/.+/i.test(url.trim());

  const hasValidUrl = isValidUrl(imageUrl);
  const resolvedImageUrl = hasValidUrl ? imageUrl.trim() : uploadedImageUri;
  const hasImage = hasValidUrl || !!uploadedImageUri;
  const isFormValid =
    !!productName.trim() &&
    hasImage &&
    !!size.trim() &&
    !!color.trim() &&
    !!needByDate;

  const handleImagePick = async () => {
    try {
      const imageUri = await openImagePicker();
      if (imageUri) {
        setUploadedImageUri(imageUri);
      }
    } catch (error) {
      SentryService.captureError('Error selecting product image', error);
      setSnackbarState({
        open: true,
        message:
          error instanceof Error
            ? error.message
            : 'Error selecting image, please try again.',
        severity: 'error',
      });
    }
  };

  const handleSubmit = async () => {
    if (!isFormValid) {
      setShowValidationErrors(true);
      return;
    }

    if (!clientUserId) {
      SentryService.captureError('SourcedByForm: Client user ID is required');
      setSnackbarState({
        open: true,
        message: 'Unable to identify the client for this channel.',
        severity: 'error',
      });
      return;
    }

    setShowValidationErrors(false);
    setIsSubmitting(true);

    try {
      let productImageUrl = resolvedImageUrl;

      // Upload local image to Cloudinary if it's not already a hosted URL
      if (!hasValidUrl && uploadedImageUri) {
        const token = await getAccessToken();
        productImageUrl = await uploadImage(token, uploadedImageUri);
      }

      await submitSourcedByRequest({
        variables: {
          input: {
            productName: productName.trim(),
            productImageUrl,
            size: size.trim(),
            color: color.trim(),
            needByDate: needByDate!.toISOString(),
            clientUserId,
          },
        },
      });

      setProductName('');
      setImageUrl('');
      setUploadedImageUri('');
      setSize('');
      setColor('');
      setNeedByDate(undefined);

      setSnackbarState({
        open: true,
        message: 'Sourcing request submitted successfully.',
        severity: 'success',
      });
      onSubmitSuccess();
    } catch (error) {
      SentryService.captureError('Error submitting SourcedBy request', error);
      setSnackbarState({
        open: true,
        message: 'Failed to submit sourcing request. Please try again.',
        severity: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFieldChange =
    (setter: (value: string) => void) =>
    (e: { nativeEvent: { text: string } }) => {
      if (showValidationErrors) {
        setShowValidationErrors(false);
      }
      setter(e.nativeEvent.text);
    };

  const dateField = (
    <DateFieldWrapper isDesktop={isDesktop}>
      <DateLabel>NEED BY DATE</DateLabel>
      <StyledDatePickerInput
        locale="en"
        label=""
        withDateFormatInLabel={false}
        placeholder="MM/DD/YYYY"
        value={needByDate}
        onChange={(date) => {
          if (showValidationErrors) {
            setShowValidationErrors(false);
          }
          setNeedByDate(date ?? undefined);
        }}
        inputMode="start"
        mode="outlined"
        style={dateInputTextStyles}
        contentStyle={dateInputContentStyles}
        outlineStyle={{
          borderColor: showValidationErrors && !needByDate ? red : mintGreenAlt,
        }}
      />
      {showValidationErrors && !needByDate && (
        <ErrorText>Need by date is required</ErrorText>
      )}
    </DateFieldWrapper>
  );

  const content = (
    <>
      <HeaderContainer>
        <Title>Sourced By</Title>
        <Description>
          Submit a product sourcing request for your client via Sourced By.
        </Description>
      </HeaderContainer>

      <FieldsContainer isDesktop={isDesktop}>
        <PaddedFieldRow isDesktop={isDesktop}>
          <FieldWrapper isDesktop={isDesktop}>
            <StaticInput
              placeholderText="Enter product name"
              labelText="PRODUCT NAME"
              value={productName}
              customStyles={inputStyles}
              labelStyle={inputLabelStyles}
              contentStyle={inputContentStyles}
              outlineStyle={{
                borderColor:
                  showValidationErrors && !productName.trim()
                    ? red
                    : mintGreenAlt,
              }}
              onChange={handleFieldChange(setProductName)}
              showLabel={true}
            />
            {showValidationErrors && !productName.trim() && (
              <ErrorText>Product name is required</ErrorText>
            )}
          </FieldWrapper>
        </PaddedFieldRow>

        <PaddedFieldRow isDesktop={isDesktop}>
          <ImageFieldWrapper isDesktop={isDesktop}>
            <ImageInputRow>
              <ImageUrlWrapper>
                <StaticInput
                  placeholderText="Paste image URL"
                  labelText="PRODUCT IMAGE URL"
                  value={imageUrl}
                  customStyles={inputStyles}
                  labelStyle={inputLabelStyles}
                  contentStyle={inputContentStyles}
                  outlineStyle={{
                    borderColor:
                      showValidationErrors && !hasImage ? red : mintGreenAlt,
                  }}
                  onChange={handleFieldChange(setImageUrl)}
                  showLabel={true}
                />
              </ImageUrlWrapper>
              <OrText>or</OrText>
              <Button
                onPress={handleImagePick}
                text="UPLOAD"
                variant="secondary"
                size="small"
                customStyle={uploadButtonStyles}
                customLabelStyle={uploadButtonLabelStyles}
              />
            </ImageInputRow>
            {showValidationErrors && !hasImage && (
              <ErrorText>Product image is required</ErrorText>
            )}
            {hasImage && (
              <ImagePreviewContainer>
                <ImagePreview source={{ uri: resolvedImageUrl }} />
                <ClearImageButton
                  accessibilityRole="button"
                  accessibilityLabel="Clear selected product image"
                  accessibilityHint="Clears the selected product image"
                  hitSlop={{ top: 4, right: 4, bottom: 4, left: 4 }}
                  onPress={() => {
                    setImageUrl('');
                    setUploadedImageUri('');
                  }}
                >
                  <ClearImageIcon
                    source={require('@/assets/images/CloseGreen.png')}
                    tintColor={green}
                  />
                </ClearImageButton>
              </ImagePreviewContainer>
            )}
          </ImageFieldWrapper>
        </PaddedFieldRow>

        <PaddedFieldRow isDesktop={isDesktop}>
          <SizeColorRow>
            <SmallFieldWrapper>
              <StaticInput
                placeholderText="Enter size"
                labelText="SIZE"
                value={size}
                customStyles={inputStyles}
                labelStyle={inputLabelStyles}
                contentStyle={inputContentStyles}
                outlineStyle={{
                  borderColor:
                    showValidationErrors && !size.trim() ? red : mintGreenAlt,
                }}
                onChange={handleFieldChange(setSize)}
                showLabel={true}
              />
              {showValidationErrors && !size.trim() && (
                <ErrorText>Size is required</ErrorText>
              )}
            </SmallFieldWrapper>

            <SmallFieldWrapper>
              <StaticInput
                placeholderText="Enter color"
                labelText="COLOR"
                value={color}
                customStyles={inputStyles}
                labelStyle={inputLabelStyles}
                contentStyle={inputContentStyles}
                outlineStyle={{
                  borderColor:
                    showValidationErrors && !color.trim() ? red : mintGreenAlt,
                }}
                onChange={handleFieldChange(setColor)}
                showLabel={true}
              />
              {showValidationErrors && !color.trim() && (
                <ErrorText>Color is required</ErrorText>
              )}
            </SmallFieldWrapper>
          </SizeColorRow>

          {isDesktop && dateField}
        </PaddedFieldRow>

        {!isDesktop && (
          <PaddedFieldRow isDesktop={false}>{dateField}</PaddedFieldRow>
        )}

        <SubmitButtonWrapper isDesktop={isDesktop}>
          <Button
            onPress={handleSubmit}
            text="SUBMIT REQUEST"
            disabled={isSubmitting || isUploading}
            loading={isSubmitting || isUploading}
            customStyle={submitButtonStyles}
            customLabelStyle={submitButtonLabelStyles}
          />
        </SubmitButtonWrapper>
      </FieldsContainer>
    </>
  );

  return (
    <Container isDesktop={isDesktop}>
      {!isDesktop ? (
        <ScrollView
          showsVerticalScrollIndicator={false}
          style={{ maxHeight: maxInputHeight }}
          contentContainerStyle={{ flexGrow: 0, justifyContent: 'flex-start' }}
        >
          {content}
        </ScrollView>
      ) : (
        content
      )}
    </Container>
  );
};

const DESKTOP_CONTAINER_HORIZONTAL_PADDING = 24;

const Container = styled.View<{ isDesktop: boolean }>(({ isDesktop }) => ({
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'flex-start',
  backgroundColor: lightGreyAlt,
  borderWidth: 1,
  borderColor: disabledLightGrey,
  borderRadius: 8,
  width: '100%',
  maxWidth: '100%',
  ...(isDesktop
    ? {
        paddingVertical: 14,
        paddingHorizontal: 18,
        marginHorizontal: 0,
        ...(IS_WEB && {
          width:
            `calc(100% - ${DESKTOP_CONTAINER_HORIZONTAL_PADDING}px)` as unknown as number,
          alignSelf: 'center',
        }),
      }
    : {
        paddingTop: 16,
        paddingBottom: 16,
        paddingHorizontal: 16,
        marginHorizontal: 4,
      }),
  minHeight: isDesktop ? 130 : 0,
}));

const HeaderContainer = styled.View({
  flexDirection: 'column',
  width: '100%',
  maxWidth: 400,
  marginBottom: 14,
});

const Title = styled(BaseText)({
  fontSize: 15,
  fontWeight: '700',
  color: green,
});

const Description = styled(BaseText)({
  fontSize: 12,
  color: lightGreen,
  marginTop: 4,
});

const FieldsContainer = styled.View<{ isDesktop: boolean }>(
  ({ isDesktop }) => ({
    flexDirection: 'column',
    width: '100%',
    gap: isDesktop ? 12 : 10,
  }),
);

const FieldRow = styled.View<{ isDesktop: boolean }>(({ isDesktop }) => ({
  flexDirection: isDesktop ? 'row' : 'column',
  alignItems: isDesktop ? 'flex-start' : 'stretch',
  width: '100%',
  gap: isDesktop ? 16 : 10,
}));

const PaddedFieldRow = styled(FieldRow)({
  paddingTop: 6,
});

const FieldWrapper = styled.View<{ isDesktop: boolean }>(({ isDesktop }) => ({
  flex: 1,
  ...(isDesktop ? {} : { width: '100%' }),
}));

const SizeColorRow = styled.View({
  flexDirection: 'row',
  flex: 1,
  gap: 16,
});

const SmallFieldWrapper = styled.View({
  flex: 1,
});

const ImageFieldWrapper = styled.View<{ isDesktop: boolean }>(
  ({ isDesktop }) => ({
    flex: 1,
    ...(isDesktop ? {} : { width: '100%' }),
  }),
);

const ImageInputRow = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  gap: 10,
});

const ImageUrlWrapper = styled.View({
  flex: 3,
  minWidth: 0,
});

const OrText = styled(BaseText)({
  fontSize: 11,
  color: lightGreen,
});

const ImagePreviewContainer = styled.View({
  flexDirection: 'row',
  alignItems: 'flex-start',
  marginLeft: 12,
  marginTop: 8,
});

const ImagePreview = styled(Image)({
  width: 60,
  height: 60,
  borderRadius: 6,
  backgroundColor: disabledLightGrey,
});

const ClearImageButton = styled.TouchableOpacity({
  width: 20,
  height: 20,
  borderRadius: 10,
  borderWidth: 1,
  backgroundColor: white,
  alignItems: 'center',
  justifyContent: 'center',
  marginLeft: -10,
  marginTop: -6,
});

const ClearImageIcon = styled(Image)({
  width: 8,
  height: 8,
});

const DateFieldWrapper = styled.View<{ isDesktop: boolean }>(
  ({ isDesktop }) => ({
    position: 'relative',
    ...(isDesktop ? { flex: 1, maxWidth: 220 } : { width: '100%' }),
  }),
);

const DateLabel = styled(BaseText)({
  fontSize: 7,
  fontWeight: 400,
  position: 'absolute',
  top: -6,
  left: 14,
  zIndex: 1000,
  backgroundColor: white,
  letterSpacing: 0.84,
  textTransform: 'uppercase',
  paddingHorizontal: 8,
  paddingVertical: 2,
  color: green,
});

const StyledDatePickerInput = styled(DatePickerInput)({
  backgroundColor: white,
  height: 35,
});

const dateInputTextStyles = {
  fontSize: 10,
};

const dateInputContentStyles = {
  fontSize: 10,
  paddingLeft: 12,
};

const SubmitButtonWrapper = styled.View<{ isDesktop: boolean }>(
  ({ isDesktop }) => ({
    ...(isDesktop
      ? { alignSelf: 'flex-end' }
      : { alignSelf: 'center', width: '100%', alignItems: 'center' }),
  }),
);

const ErrorText = styled(BaseText)({
  fontSize: 10,
  color: red,
  marginTop: 4,
  alignSelf: 'flex-start',
});

const inputStyles = {
  height: 35,
  fontSize: 10,
};

const inputContentStyles = {
  fontSize: 10,
  paddingLeft: 4,
};

const inputLabelStyles = {
  fontSize: 7,
  left: 14,
};

const submitButtonStyles = {
  height: 46,
  minWidth: 160,
};

const submitButtonLabelStyles = {
  fontSize: 10,
  letterSpacing: 0.5,
};

const uploadButtonStyles = {
  height: 35,
  width: 80,
};

const uploadButtonLabelStyles = {
  fontSize: 8,
  letterSpacing: 0.41,
};
